import type { RuleDrive, RuleTreeNode, RulesReply, Scoped } from '../api-types';

export const GENERAL = 'general';

/** The filter's value in the address: `general` (default) or an area code. */
export function areaFrom(raw: string | null): string {
  return raw === null || raw === '' ? GENERAL : raw;
}

/** What each area chip is: «Generali» first, then the areas the caller may see, in order. */
export function chipsOf(reply: RulesReply): string[] {
  const areas = [...reply.areas].sort();
  return [GENERAL, ...areas.filter((area) => area !== GENERAL)];
}

/** The caller holds this area, or it is the general one. */
export function isEnabled(reply: RulesReply, area: string): boolean {
  return area === GENERAL || reply.areas.includes(area);
}

/** The areas a part belongs to: `scopes` when it lists several, else its one `scope`. */
export function areasOf(part: Scoped): readonly string[] {
  return part.scopes !== undefined && part.scopes.length > 0 ? part.scopes : [part.scope];
}

/** A part belongs to this area chip. */
export function inScope(part: Scoped, area: string): boolean {
  return areasOf(part).includes(area);
}

/** The rules of one area: only the parts whose scope is, or lists, that area. */
export function inArea(reply: RulesReply, area: string) {
  const keep = <T extends Scoped>(items: readonly T[]): T[] =>
    items.filter((item) => inScope(item, area));
  const drives: RuleDrive[] = keep(reply.drives);
  return {
    drives,
    naming: inScope(reply.naming, area) ? reply.naming : undefined,
    types: keep(reply.types),
    codes: keep(reply.codes),
    conventions: keep(reply.conventions),
  };
}

export type AreaRules = ReturnType<typeof inArea>;

export const isEmptyArea = (rules: AreaRules): boolean =>
  rules.drives.length === 0 &&
  rules.naming === undefined &&
  rules.types.length === 0 &&
  rules.codes.length === 0 &&
  rules.conventions.length === 0;

/**
 * The area chip that shows the section an anchor names, to switch the filter to it. A part of
 * several areas stays on the current chip when it is one of them, else the first area the caller
 * holds.
 */
export function scopeOfSection(
  reply: RulesReply,
  section: string,
  current: string = GENERAL,
): string | undefined {
  const pick = (part: Scoped): string => {
    const areas = areasOf(part);
    if (areas.includes(current)) return current;
    return areas.find((area) => reply.areas.includes(area)) ?? part.scope;
  };
  const all: (Scoped & { readonly section: string })[] = [
    ...reply.drives,
    reply.naming,
    ...reply.naming.parts,
    ...reply.types,
    ...reply.codes,
    ...reply.conventions,
  ];
  // Tree nodes carry no scope: a node belongs to its drive's.
  const inTree = (nodes: readonly RuleTreeNode[]): boolean =>
    nodes.some((node) => node.section === section || inTree(node.children ?? []));
  const direct = all.find((entry) => entry.section === section);
  if (direct !== undefined) return pick(direct);
  const drive = reply.drives.find((d) => inTree(d.tree));
  return drive === undefined ? undefined : pick(drive);
}
